import { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import {
  getWhatsAppTemplates,
  createWhatsAppTemplate,
  validateWhatsAppTemplate,
  sendBulkMessages,
  getArchivedWhatsAppTemplates,
  setWhatsAppTemplateArchived,
  syncWhatsAppTemplates,
} from '../api';
import { TemplateCreateForm } from '../components/TemplateCreateForm';
import { SendMessagePage } from '../components/SendMessagePage';
import { TemplatePhonePreview } from '../components/TemplatePhonePreview';
import { buildBulkMessagePayload } from '../utils/buildBulkMessagePayload';
import {
  Button,
  Modal,
  SearchInput,
  CustomSelect,
  SideDrawer,
  SkeletonText,
  StatusBadge,
} from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { parseApiError } from '@/common/utils';
import { encodeId } from '@/common/utils/encryption';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  CopyIcon,
  ArchiveIcon,
  RestoreIcon,
} from '../components/Icons';
import { DashboardPage } from '../components/PremiumDesign';
import {
  useWhatsAppConnection,
  WHATSAPP_REQUEST_TIMEOUT_MS,
} from '../hooks/useWhatsAppConnection';
import {
  ListRequestError,
  WhatsAppListPrerequisite,
} from '../components/WhatsAppListPrerequisite';
import sendIcon from '../assets/Send.png';
import eyeIcon from '../assets/eye.png';
import archivePopupImg from '../assets/archivePopup.png';
import { PermissionGuard, V2Table } from '@/common/components';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';

const TEMPLATE_TYPE_OPTIONS = [
  { value: 'ALL', label: 'All Types' },
  { value: 'UTILITY', label: 'Notification' },
  { value: 'MARKETING', label: 'Promotion' },
  { value: 'AUTHENTICATION', label: 'Verification' },
];
export function WhatsAppTemplatePage() {
  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const { can } = usePermissions();

  const companyId = getCompanyId();
  const whatsapp = useWhatsAppConnection(companyId);
  const whatsappReady = whatsapp.phase === 'connected';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [viewMode, setViewMode] = useState('list'); // 'list' | 'create' | 'send'
  const [sendSelectionKey, setSendSelectionKey] = useState(0);
  const [selectedPreviewTemplate, setSelectedPreviewTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [selectedTemplateForMessage, setSelectedTemplateForMessage] =
    useState(null);
  const [selectedType, setSelectedType] = useState('ALL');
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'archived'
  const [templateToArchive, setTemplateToArchive] = useState(null); // template object | null
  const [templateToUnarchive, setTemplateToUnarchive] = useState(null); // template object | null

  // Debounce search so we hit the API `search` param dynamically while typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch templates (server-side search via `search` & `category` query params)
  const {
    data: templatesRaw,
    isLoading: isTemplatesInitialLoading,
    isFetching: isTemplatesFetching,
    isError,
    error,
    refetch: refetchTemplates,
  } = useQuery({
    queryKey: ['whatsapp-templates', companyId, debouncedSearch, selectedType],
    enabled: whatsappReady,
    retry: false,
    queryFn: ({ signal }) =>
      getWhatsAppTemplates(
        companyId,
        {
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
          ...(selectedType && selectedType !== 'ALL'
            ? { category: selectedType }
            : {}),
        },
        { signal, timeout: WHATSAPP_REQUEST_TIMEOUT_MS },
      ),
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isTemplatesFetching && templatesRaw !== undefined,
    300,
    500,
  );
  const isLoading = isTemplatesInitialLoading || showDelayedSkeleton;

  useEffect(() => {
    if (isError) {
      const { message } = parseApiError(error);
      toast.error(message || 'Failed to fetch templates.');
    }
  }, [isError, error]);

  const createTemplateMutation = useMutation({
    mutationFn: async (payload) => {
      if (!can(PERMISSIONS.WA_TEMPLATES.CREATE)) {
        toast.error('You do not have permission to perform this action.');
        return Promise.reject(new Error('Unauthorized'));
      }
      if (payload?.template_type === 'CAROUSEL') {
        await validateWhatsAppTemplate({ companyId, payload });
      }
      return createWhatsAppTemplate({ companyId, payload });
    },
    onSuccess: (data) => {
      if (data?.status === 'EXISTING') {
        toast.error(
          'Template already exists. Change the template name and try again.',
        );
        return;
      }
      toast.success('Template created successfully');
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-templates', companyId],
      });
      setViewMode('list');
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to create template. Please try again.');
    },
  });

  // Fetch archived templates (lazy — only when archived tab is open)
  const {
    data: archivedRaw,
    isLoading: isArchivedLoading,
    isError: isArchivedError,
    refetch: refetchArchived,
  } = useQuery({
    queryKey: ['whatsapp-templates-archived', companyId],
    queryFn: ({ signal }) =>
      getArchivedWhatsAppTemplates(
        companyId,
        {},
        {
          signal,
          timeout: WHATSAPP_REQUEST_TIMEOUT_MS,
        },
      ),
    enabled: whatsappReady && activeTab === 'archived' && viewMode === 'list',
    retry: false,
    placeholderData: keepPreviousData,
  });

  const archivedList = useMemo(
    () =>
      archivedRaw?.results || (Array.isArray(archivedRaw) ? archivedRaw : []),
    [archivedRaw],
  );

  // Single mutation for archive & unarchive
  const syncTemplatesMutation = useMutation({
    mutationFn: () => syncWhatsAppTemplates(companyId),
    onSuccess: (data) => {
      const count = data?.synced_count;
      toast.success(
        count != null
          ? `Synced ${count} template${count === 1 ? '' : 's'} from Meta`
          : 'Templates synced with Meta',
      );
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-templates', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-templates-archived', companyId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to sync templates with Meta.');
    },
  });

  const archiveToggleMutation = useMutation({
    mutationFn: ({ templateName, isArchived }) =>
      setWhatsAppTemplateArchived({ companyId, templateName, isArchived }),
    onSuccess: (_, { isArchived }) => {
      toast.success(
        isArchived ? 'Template archived.' : 'Template restored to active.',
      );
      setTemplateToArchive(null);
      setTemplateToUnarchive(null);
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-templates', companyId],
      });
      queryClient.invalidateQueries({
        queryKey: ['whatsapp-templates-archived', companyId],
      });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update template.');
    },
  });

  const sendBulkMessagesMutation = useMutation({
    mutationFn: (payload) => sendBulkMessages({ companyId, payload }),
    onSuccess: (data, variables) => {
      const mode = variables?.sending_mode || 'send_now';
      const total = data?.total;
      const sentCount = data?.sent_count;
      const failedCount = data?.failed_count;
      const totalBit = total != null ? ` · ${total} recipients` : '';

      if (mode === 'send_now') {
        if (sentCount != null) {
          toast.success(
            `Template sent successfully to ${sentCount} recipient(s).`,
          );
          if (failedCount > 0) {
            toast.error(`${failedCount} recipient(s) failed.`);
          }
        } else {
          toast.success(`Bulk messages queued${totalBit}`);
        }
      } else if (mode === 'schedule_once') {
        toast.success(`Campaign scheduled${totalBit}`);
      } else {
        toast.success(`Recurring campaign created${totalBit}`);
      }
      setSendSelectionKey((k) => k + 1);
      setViewMode('list');
      setSelectedTemplateForMessage(null);
      const campaignId = data?.id || data?.campaign_id;
      if (campaignId) {
        navigate(`/campaigns/${encodeId(campaignId)}`);
      }
    },
    onError: (err) => {
      const yearMsg = err?.response?.data?.year;
      if (yearMsg) return;
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to create campaign. Please try again.');
    },
  });

  const templateList = useMemo(
    () =>
      templatesRaw?.results ||
      (Array.isArray(templatesRaw) ? templatesRaw : []),
    [templatesRaw],
  );

  const filteredTemplates = useMemo(() => {
    if (!selectedType || selectedType === 'ALL') return templateList;
    return templateList.filter((template) => {
      const cat = (template.category || '').toUpperCase();
      return cat === selectedType;
    });
  }, [templateList, selectedType]);

  const handleCopyId = useCallback((e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    toast.success('Template ID copied to clipboard!');
  }, []);

  const handleSendTrigger = useCallback(
    (e, id) => {
      e.stopPropagation();
      if (!can(PERMISSIONS.WA_CAMPAIGNS.CREATE)) {
        toast.error(
          'Access Denied: You do not have permission to send campaigns.',
        );
        return;
      }
      setSelectedTemplateForMessage(id);
      setSelectedPreviewTemplate(null);
      setViewMode('send');
    },
    [can],
  );

  const executeSendOrSchedule = async (customerIds, messagePayload) => {
    const ids = customerIds || [];
    const selection = messagePayload?.selection;
    const selections = messagePayload?.selections;
    const contactNumbers = messagePayload?.contact_numbers || [];
    if (
      !selection &&
      !selections?.length &&
      ids.length === 0 &&
      contactNumbers.length === 0 &&
      !messagePayload?.year
    ) {
      toast.error(
        'Please select at least one contact or enter a direct phone number.',
      );
      return;
    }

    const payload = buildBulkMessagePayload({
      templateId: selectedTemplateForMessage,
      messagePayload,
    });
    if (selections?.length) payload.selections = selections;
    else if (selection) payload.selection = selection;
    if (ids.length > 0) payload.customer_ids = ids;
    if (contactNumbers.length > 0) payload.contact_numbers = contactNumbers;
    if (messagePayload?.year) {
      payload.year = messagePayload.year;
      if (messagePayload.studentClass)
        payload.class = messagePayload.studentClass;
    }
    if (messagePayload?.customer_type) {
      payload.customer_type = messagePayload.customer_type;
    }
    if (messagePayload?.segment) {
      payload.segment = messagePayload.segment;
    }
    if (messagePayload?.recipient_variables) {
      payload.recipient_variables = messagePayload.recipient_variables;
    }

    return sendBulkMessagesMutation.mutateAsync(payload);
  };

  const handleRowClick = useCallback((template) => {
    setSelectedPreviewTemplate(template);
  }, []);

  // Get active template categories
  const getSelectedTemplateCategoryLabel = (template) => {
    if (!template) return '';
    const cat = (template.category || '').toUpperCase();
    if (cat === 'MARKETING') return 'Promotion';
    if (cat === 'AUTHENTICATION') return 'Verification';
    if (cat === 'UTILITY') return 'Notification';
    return 'General';
  };

  const templateStatus = useCallback(
    (template) => (
      <div className="flex flex-col items-center justify-center gap-0.5 min-w-0">
        <StatusBadge
          tone={
            template.status === 'APPROVED'
              ? 'success'
              : template.status === 'REJECTED'
                ? 'error'
                : 'warning'
          }
        >
          {template.status === 'APPROVED'
            ? 'Active'
            : template.status || 'Pending'}
        </StatusBadge>
        {template.status === 'REJECTED' &&
          template.rejected_reason &&
          template.rejected_reason !== 'NONE' && (
            <span
              className="text-tiny text-red-500 italic font-medium truncate max-w-[140px]"
              title={template.rejected_reason}
            >
              {template.rejected_reason}
            </span>
          )}
      </div>
    ),
    [],
  );

  const activeTableColumns = useMemo(() => {
    const showActions = can(PERMISSIONS.WA_CAMPAIGNS.CREATE);
    const cols = [
      {
        id: 'channel',
        label: 'Channel',
        defaultWidth: 140,
        minWidth: 110,
        resizable: true,
        align: 'center',
        skeleton: <SkeletonText width="w-16" className="mx-auto" />,
        cell: (template) => (
          <span
            className="truncate block font-medium text-sm text-[#0F172A]"
            title={template.channel || 'WhatsApp'}
          >
            {template.channel || 'WhatsApp'}
          </span>
        ),
      },
      {
        id: 'templateId',
        label: 'Template ID',
        defaultWidth: 220,
        minWidth: 160,
        resizable: true,
        align: 'center',
        skeleton: (
          <div className="flex items-center justify-center gap-1.5">
            <SkeletonText width="w-24" />
            <SkeletonText width="w-4" />
          </div>
        ),
        cell: (template) => (
          <div className="flex items-center justify-center gap-1.5 min-w-0">
            <span
              className="truncate font-mono text-sm text-[#1E293B]"
              title={String(template.id ?? '')}
            >
              {template.id}
            </span>
            <button
              type="button"
              onClick={(e) => handleCopyId(e, template.id)}
              className="p-1 hover:bg-[#F1F5F9] rounded-md transition-colors text-text-muted hover:text-text-primary focus:outline-none shrink-0"
              title="Copy Template ID"
            >
              <CopyIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
      },
      {
        id: 'name',
        label: 'Name',
        defaultWidth: 260,
        minWidth: 160,
        resizable: true,
        align: 'left',
        skeleton: <SkeletonText width="w-32" />,
        cell: (template) => (
          <span
            className="truncate block font-medium text-sm text-[#0F172A] hover:text-[#2E7D32] transition-colors"
            title={template.name || ''}
          >
            {template.name}
          </span>
        ),
      },
      {
        id: 'type',
        label: 'Type',
        defaultWidth: 160,
        minWidth: 120,
        resizable: true,
        align: 'left',
        skeleton: <SkeletonText width="w-20" />,
        cell: (template) => (
          <span className="truncate block text-sm text-[#1E293B]">
            {getSelectedTemplateCategoryLabel(template)}
          </span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 150,
        minWidth: 120,
        resizable: true,
        align: 'center',
        skeleton: (
          <SkeletonText
            width="w-16"
            height="h-6"
            className="!rounded-full mx-auto"
          />
        ),
        cell: templateStatus,
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        label: 'Actions',
        defaultWidth: 150,
        minWidth: 130,
        resizable: false,
        align: 'center',
        stopPropagation: true,
        skeleton: (
          <div className="flex items-center justify-center gap-2">
            <SkeletonText width="w-5" height="h-5" />
            <SkeletonText width="w-5" height="h-5" />
            <SkeletonText width="w-5" height="h-5" />
          </div>
        ),
        cell: (template) => (
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => handleRowClick(template)}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#F1F5F9] rounded-lg transition-colors focus:outline-none shrink-0"
              title="View Template"
            >
              <img
                src={eyeIcon}
                alt="View"
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
            <button
              type="button"
              onClick={(e) => handleSendTrigger(e, template.id)}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#F1F5F9] rounded-lg transition-colors focus:outline-none shrink-0"
              title="Send Template"
            >
              <img
                src={sendIcon}
                alt="Send"
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
            <button
              type="button"
              onClick={() => setTemplateToArchive(template)}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#FEEFEF] rounded-lg transition-colors focus:outline-none shrink-0 text-amber-500 hover:text-amber-600"
              title="Archive Template"
            >
              <ArchiveIcon className="w-5 h-5" />
            </button>
          </div>
        ),
      });
    }

    return cols;
  }, [can, handleCopyId, handleRowClick, handleSendTrigger, templateStatus]);

  const archivedTableColumns = useMemo(() => {
    return [
      {
        id: 'channel',
        label: 'Channel',
        defaultWidth: 140,
        minWidth: 110,
        resizable: true,
        align: 'center',
        skeleton: <SkeletonText width="w-16" className="mx-auto" />,
        cell: (template) => (
          <span
            className="truncate block font-medium text-sm text-[#0F172A]"
            title={template.channel || 'WhatsApp'}
          >
            {template.channel || 'WhatsApp'}
          </span>
        ),
      },
      {
        id: 'templateId',
        label: 'Template ID',
        defaultWidth: 220,
        minWidth: 160,
        resizable: true,
        align: 'center',
        skeleton: (
          <div className="flex items-center justify-center gap-1.5">
            <SkeletonText width="w-24" />
            <SkeletonText width="w-4" />
          </div>
        ),
        cell: (template) => (
          <div className="flex items-center justify-center gap-1.5 min-w-0">
            <span
              className="truncate font-mono text-sm text-[#1E293B]"
              title={String(template.id ?? '')}
            >
              {template.id}
            </span>
            <button
              type="button"
              onClick={(e) => handleCopyId(e, template.id)}
              className="p-1 hover:bg-[#F1F5F9] rounded-md transition-colors text-text-muted hover:text-text-primary focus:outline-none shrink-0"
              title="Copy Template ID"
            >
              <CopyIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        ),
      },
      {
        id: 'name',
        label: 'Name',
        defaultWidth: 260,
        minWidth: 160,
        resizable: true,
        align: 'left',
        skeleton: <SkeletonText width="w-32" />,
        cell: (template) => (
          <span
            className="truncate block font-medium text-sm text-[#0F172A] hover:text-[#2E7D32] transition-colors"
            title={template.name || ''}
          >
            {template.name}
          </span>
        ),
      },
      {
        id: 'type',
        label: 'Type',
        defaultWidth: 160,
        minWidth: 120,
        resizable: true,
        align: 'left',
        skeleton: <SkeletonText width="w-20" />,
        cell: (template) => (
          <span className="truncate block text-sm text-[#1E293B]">
            {getSelectedTemplateCategoryLabel(template)}
          </span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 150,
        minWidth: 120,
        resizable: true,
        align: 'center',
        skeleton: (
          <SkeletonText
            width="w-16"
            height="h-6"
            className="!rounded-full mx-auto"
          />
        ),
        cell: templateStatus,
      },
      {
        id: 'actions',
        label: 'Actions',
        defaultWidth: 140,
        minWidth: 120,
        resizable: false,
        align: 'center',
        stopPropagation: true,
        skeleton: (
          <div className="flex items-center justify-center gap-2">
            <SkeletonText width="w-5" height="h-5" />
            <SkeletonText width="w-5" height="h-5" />
          </div>
        ),
        cell: (template) => (
          <div className="flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => setSelectedPreviewTemplate(template)}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#F1F5F9] rounded-lg transition-colors focus:outline-none shrink-0"
              title="Preview Template"
            >
              <img
                src={eyeIcon}
                alt="Preview"
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
            <button
              type="button"
              onClick={() => setTemplateToUnarchive(template)}
              disabled={archiveToggleMutation.isPending}
              className="w-8 h-8 flex items-center justify-center hover:bg-[#EBF4EA] rounded-lg transition-colors focus:outline-none shrink-0 text-[#50AB3D] hover:text-[#439632] disabled:opacity-50"
              title="Restore Template"
            >
              <RestoreIcon className="w-5 h-5" />
            </button>
          </div>
        ),
      },
    ];
  }, [archiveToggleMutation.isPending, handleCopyId, templateStatus]);

  return (
    <DashboardPage
      className={
        viewMode === 'list'
          ? 'templates-page h-full flex-1 min-h-0 flex flex-col overflow-hidden'
          : ''
      }
    >
      {viewMode === 'list' && (
        <div className="flex flex-col w-full h-full flex-1 min-h-0 overflow-hidden">
          {/* Tab Bar */}
          <div className="flex gap-0 mb-4 border-b border-border-default shrink-0">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                activeTab === 'active'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setActiveTab('archived')}
              className={`px-4 py-2 text-sm font-medium transition-colors focus:outline-none ${
                activeTab === 'archived'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-text-muted hover:text-text-primary'
              }`}
            >
              Archived
            </button>
          </div>

          {/* Search + Filter + Create Row — only on Active tab */}
          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 shrink-0 ${
              activeTab === 'archived' ? 'hidden' : ''
            }`}
          >
            <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto sm:flex-1 sm:max-w-xl">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Name"
                className="w-full sm:max-w-md"
              />
              <CustomSelect
                value={selectedType}
                onChange={setSelectedType}
                options={TEMPLATE_TYPE_OPTIONS}
                placeholder="All Types"
                className="w-full sm:w-[170px] h-10 shrink-0"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
              <PermissionGuard permission={PERMISSIONS.WA_TEMPLATES.VIEW}>
                <Button
                  variant="secondary"
                  onClick={() => syncTemplatesMutation.mutate()}
                  disabled={syncTemplatesMutation.isPending}
                  className="h-[40px] px-4 w-full sm:w-auto shrink-0"
                >
                  <span>
                    {syncTemplatesMutation.isPending
                      ? 'Syncing...'
                      : 'Sync with Meta'}
                  </span>
                </Button>
              </PermissionGuard>
              <PermissionGuard permission={PERMISSIONS.WA_TEMPLATES.CREATE}>
                <Button
                  variant="primary"
                  onClick={() => {
                    setSelectedPreviewTemplate(null);
                    setViewMode('create');
                  }}
                  className="h-[40px] px-4 w-full sm:w-auto shrink-0"
                  icon={<PlusIcon />}
                >
                  <span>Create Template</span>
                </Button>
              </PermissionGuard>
            </div>
          </div>

          {/* Active Tab Table */}
          <div
            className={`w-full min-w-0 flex-1 min-h-0 flex flex-col overflow-hidden ${
              activeTab === 'archived' ? 'hidden' : ''
            }`}
          >
            <WhatsAppListPrerequisite connection={whatsapp}>
              {isError ? (
                <ListRequestError
                  message="Could not load templates. The request failed or timed out."
                  onRetry={() => refetchTemplates()}
                />
              ) : (
                <V2Table
                  tight
                  columns={activeTableColumns}
                  data={filteredTemplates}
                  rowKey={(row) => row.id || row.name}
                  storageKey="whatsapp_templates_column_widths"
                  loading={whatsappReady && isLoading}
                  loadingRowCount={10}
                  isRowSelected={(template) =>
                    selectedPreviewTemplate?.id === template.id
                  }
                  onRowClick={handleRowClick}
                  empty={
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="w-14 h-14 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full flex items-center justify-center">
                        <svg
                          className="w-7 h-7 text-[#94A3B8]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-[#475569]">
                        {searchQuery || (selectedType && selectedType !== 'ALL')
                          ? 'No templates match your filters'
                          : 'No templates found'}
                      </span>
                    </div>
                  }
                />
              )}
            </WhatsAppListPrerequisite>
          </div>

          {/* Archived Tab Table */}
          {activeTab === 'archived' && (
            <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col overflow-hidden">
              <WhatsAppListPrerequisite connection={whatsapp}>
                {isArchivedError ? (
                  <ListRequestError
                    message="Could not load templates. The request failed or timed out."
                    onRetry={() => refetchArchived()}
                  />
                ) : (
                  <V2Table
                    tight
                    columns={archivedTableColumns}
                    data={archivedList}
                    rowKey={(row) => row.id || row.name}
                    storageKey="whatsapp_templates_archived_column_widths"
                    loading={whatsappReady && isArchivedLoading}
                    loadingRowCount={5}
                    isRowSelected={(template) =>
                      selectedPreviewTemplate?.id === template.id
                    }
                    onRowClick={(template) =>
                      setSelectedPreviewTemplate(template)
                    }
                    empty={
                      <div className="flex flex-col items-center justify-center gap-3">
                        <div className="w-14 h-14 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full flex items-center justify-center">
                          <ArchiveIcon className="w-7 h-7 text-[#94A3B8]" />
                        </div>
                        <span className="text-sm font-medium text-[#475569]">
                          No archived templates
                        </span>
                      </div>
                    }
                  />
                )}
              </WhatsAppListPrerequisite>
            </div>
          )}

          {/* Archive Confirm Modal */}
          <Modal
            isOpen={!!templateToArchive}
            onClose={() => {
              if (!archiveToggleMutation.isPending) setTemplateToArchive(null);
            }}
            showClose={false}
            size="sm"
            panelClassName="templates-dialog p-5 sm:p-6 rounded-[24px]"
            zIndexClass="z-[110]"
          >
            {/* Header inside padded panel */}
            <div className="flex items-center justify-between pb-3 border-b border-[#F0F0F0] mb-4 -mx-5 sm:-mx-6 px-5 sm:px-6">
              <h2 className="text-[20px] font-medium text-[#1F2241]">
                Archive Template
              </h2>
              <button
                type="button"
                onClick={() => {
                  if (!archiveToggleMutation.isPending)
                    setTemplateToArchive(null);
                }}
                disabled={archiveToggleMutation.isPending}
                className="w-8 h-8 rounded-[8px] bg-[#50AB3D] text-white flex items-center justify-center hover:opacity-90 transition-opacity focus:outline-none shrink-0 disabled:opacity-50"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal content body */}
            <div className="flex flex-col items-center text-center">
              <img
                src={archivePopupImg}
                alt="Archive"
                className="w-[160px] h-[160px] object-contain mb-3"
                loading="eager"
              />

              <h3 className="text-[20px] font-medium text-[#1F2241] leading-snug">
                Are you sure you want to archive
                <br />
                <span className="text-[#50AB3D]">
                  {templateToArchive?.name}
                </span>
                ?
              </h3>

              <p className="text-[14px] text-[#868686] mt-2 max-w-[280px] leading-normal font-normal">
                It will be hidden from active lists and template pickers.
              </p>

              <div className="w-full flex flex-col gap-2 mt-5">
                <Button
                  variant="danger"
                  onClick={() => {
                    if (templateToArchive) {
                      archiveToggleMutation.mutate({
                        templateName: templateToArchive.name,
                        isArchived: true,
                      });
                    }
                  }}
                  disabled={archiveToggleMutation.isPending}
                  className="h-11 px-6 rounded-[12px] font-semibold text-sm flex items-center justify-center gap-2 w-full"
                >
                  <span>
                    {archiveToggleMutation.isPending
                      ? 'Archiving...'
                      : 'Archive'}
                  </span>
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setTemplateToArchive(null)}
                  disabled={archiveToggleMutation.isPending}
                  className="h-11 px-6 rounded-[12px] font-semibold text-sm w-full"
                >
                  <span>Cancel</span>
                </Button>
              </div>
            </div>
          </Modal>

          {/* Unarchive Confirm Modal */}
          <Modal
            isOpen={!!templateToUnarchive}
            onClose={() => {
              if (!archiveToggleMutation.isPending)
                setTemplateToUnarchive(null);
            }}
            showClose={false}
            size="sm"
            panelClassName="templates-dialog p-5 sm:p-6 rounded-[24px]"
            zIndexClass="z-[110]"
          >
            {/* Header inside padded panel */}
            <div className="flex items-center justify-between pb-3 border-b border-[#F0F0F0] mb-4 -mx-5 sm:-mx-6 px-5 sm:px-6">
              <h2 className="text-[20px] font-medium text-[#1F2241]">
                Restore Template
              </h2>
              <button
                type="button"
                onClick={() => {
                  if (!archiveToggleMutation.isPending)
                    setTemplateToUnarchive(null);
                }}
                disabled={archiveToggleMutation.isPending}
                className="w-8 h-8 rounded-[8px] bg-[#50AB3D] text-white flex items-center justify-center hover:opacity-90 transition-opacity focus:outline-none shrink-0 disabled:opacity-50"
                aria-label="Close"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal content body */}
            <div className="flex flex-col items-center text-center">
              <img
                src={archivePopupImg}
                alt="Restore"
                className="w-[160px] h-[160px] object-contain mb-3"
                loading="eager"
              />

              <h3 className="text-[20px] font-medium text-[#1F2241] leading-snug">
                Are you sure you want to restore
                <br />
                <span className="text-[#50AB3D]">
                  {templateToUnarchive?.name}
                </span>
                ?
              </h3>

              <p className="text-[14px] text-[#868686] mt-2 max-w-[280px] leading-normal font-normal">
                It will be restored to your active templates list.
              </p>

              <div className="w-full flex flex-col gap-2 mt-5">
                <Button
                  variant="primary"
                  onClick={() => {
                    if (templateToUnarchive) {
                      archiveToggleMutation.mutate({
                        templateName: templateToUnarchive.name,
                        isArchived: false,
                      });
                    }
                  }}
                  disabled={archiveToggleMutation.isPending}
                  className="h-11 px-6 rounded-[12px] font-semibold text-sm flex items-center justify-center gap-2 w-full"
                >
                  <span>
                    {archiveToggleMutation.isPending
                      ? 'Restoring...'
                      : 'Restore'}
                  </span>
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setTemplateToUnarchive(null)}
                  disabled={archiveToggleMutation.isPending}
                  className="h-11 px-6 rounded-[12px] font-semibold text-sm w-full"
                >
                  <span>Cancel</span>
                </Button>
              </div>
            </div>
          </Modal>

          {/* Template Preview Drawer */}
          <SideDrawer
            className="templates-panel"
            isOpen={!!selectedPreviewTemplate}
            onClose={() => setSelectedPreviewTemplate(null)}
            width="sm"
            showClose={false}
            headerClassName="!px-6 !py-5"
            bodyClassName="p-3 sm:p-4 overflow-y-auto visible-scrollbar laptop:p-2"
            header={
              <div className="flex items-center justify-between w-full gap-3">
                <div className="flex flex-col text-left">
                  <span className="text-[18px] leading-[26px] font-semibold text-[#1F2241] font-sf-pro-display">
                    {selectedPreviewTemplate?.channel || 'WhatsApp'}
                  </span>
                  <span className="text-xxs text-text-muted leading-none">
                    {selectedPreviewTemplate &&
                      getSelectedTemplateCategoryLabel(selectedPreviewTemplate)}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {activeTab !== 'archived' && (
                    <PermissionGuard
                      permission={PERMISSIONS.WA_CAMPAIGNS.CREATE}
                    >
                      <button
                        type="button"
                        onClick={(e) =>
                          handleSendTrigger(e, selectedPreviewTemplate?.id)
                        }
                        className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-layer-7-hover transition-all"
                        title="Send Template"
                      >
                        <img
                          src={sendIcon}
                          alt=""
                          className="w-6 h-6 object-contain"
                        />
                      </button>
                    </PermissionGuard>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedPreviewTemplate(null)}
                    className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
                    aria-label="Close"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.75"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
            }
          >
            {selectedPreviewTemplate && (
              <div className="flex justify-center items-start py-3 pb-8 laptop:py-0 laptop:pb-0 w-full">
                <div className="relative mx-auto w-[290px] sm:w-[320px] xl:w-[340px] h-[520px] sm:h-[640px] max-w-full laptop:overflow-hidden laptop:!h-[min(512px,calc(100dvh-190px))] laptop:!w-[calc(min(512px,calc(100dvh-190px))*340/640)]">
                  <div className="laptop:absolute laptop:left-1/2 laptop:top-0 laptop:origin-top laptop:-ml-[170px] laptop:scale-[0.8]">
                    <TemplatePhonePreview
                      template={selectedPreviewTemplate}
                      className="laptop:!w-[340px] laptop:!h-[640px]"
                    />
                  </div>
                </div>
              </div>
            )}
          </SideDrawer>
        </div>
      )}

      {viewMode === 'create' && (
        <TemplateCreateForm
          onCancel={() => {
            setViewMode('list');
            setSelectedPreviewTemplate(null);
          }}
          companyId={companyId}
          createTemplateMutation={createTemplateMutation}
        />
      )}

      {viewMode === 'send' && (
        <SendMessagePage
          key={sendSelectionKey}
          companyId={companyId}
          templateName={
            templateList?.find((t) => t.id === selectedTemplateForMessage)?.name
          }
          template={templateList?.find(
            (t) => t.id === selectedTemplateForMessage,
          )}
          onCancel={() => {
            setViewMode('list');
            setSelectedTemplateForMessage(null);
          }}
          onSend={executeSendOrSchedule}
          isSending={sendBulkMessagesMutation.isPending}
        />
      )}
    </DashboardPage>
  );
}
